/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  CodeInterpreterToolDefinition,
  CodeInterpreterToolDefinition$inboundSchema,
  CodeInterpreterToolDefinition$Outbound,
  CodeInterpreterToolDefinition$outboundSchema,
} from "./codeinterpretertooldefinition.js";
import {
  CompactionContextManagement,
  CompactionContextManagement$inboundSchema,
  CompactionContextManagement$Outbound,
  CompactionContextManagement$outboundSchema,
} from "./compactioncontextmanagement.js";
import {
  ComputerUseToolDefinition,
  ComputerUseToolDefinition$inboundSchema,
  ComputerUseToolDefinition$Outbound,
  ComputerUseToolDefinition$outboundSchema,
} from "./computerusetooldefinition.js";
import {
  Conversation,
  Conversation$inboundSchema,
  Conversation$Outbound,
  Conversation$outboundSchema,
} from "./conversation.js";
import {
  CustomToolDefinition,
  CustomToolDefinition$inboundSchema,
  CustomToolDefinition$Outbound,
  CustomToolDefinition$outboundSchema,
} from "./customtooldefinition.js";
import {
  FileSearchToolDefinition,
  FileSearchToolDefinition$inboundSchema,
  FileSearchToolDefinition$Outbound,
  FileSearchToolDefinition$outboundSchema,
} from "./filesearchtooldefinition.js";
import {
  FunctionToolDefinition,
  FunctionToolDefinition$inboundSchema,
  FunctionToolDefinition$Outbound,
  FunctionToolDefinition$outboundSchema,
} from "./functiontooldefinition.js";
import {
  HistoryToolDefinition,
  HistoryToolDefinition$inboundSchema,
  HistoryToolDefinition$Outbound,
  HistoryToolDefinition$outboundSchema,
} from "./historytooldefinition.js";
import {
  HostedToolDefinition,
  HostedToolDefinition$inboundSchema,
  HostedToolDefinition$Outbound,
  HostedToolDefinition$outboundSchema,
} from "./hostedtooldefinition.js";
import {
  ImageGenerationToolDefinition,
  ImageGenerationToolDefinition$inboundSchema,
  ImageGenerationToolDefinition$Outbound,
  ImageGenerationToolDefinition$outboundSchema,
} from "./imagegenerationtooldefinition.js";
import {
  LocalShellToolDefinition,
  LocalShellToolDefinition$inboundSchema,
  LocalShellToolDefinition$Outbound,
  LocalShellToolDefinition$outboundSchema,
} from "./localshelltooldefinition.js";
import {
  MCPToolDefinition,
  MCPToolDefinition$inboundSchema,
  MCPToolDefinition$Outbound,
  MCPToolDefinition$outboundSchema,
} from "./mcptooldefinition.js";
import {
  PromptTemplate,
  PromptTemplate$inboundSchema,
  PromptTemplate$Outbound,
  PromptTemplate$outboundSchema,
} from "./prompttemplate.js";
import {
  ReasoningConfiguration,
  ReasoningConfiguration$inboundSchema,
  ReasoningConfiguration$Outbound,
  ReasoningConfiguration$outboundSchema,
} from "./reasoningconfiguration.js";
import {
  ShellToolDefinition,
  ShellToolDefinition$inboundSchema,
  ShellToolDefinition$Outbound,
  ShellToolDefinition$outboundSchema,
} from "./shelltooldefinition.js";
import {
  SleepToolDefinition,
  SleepToolDefinition$inboundSchema,
  SleepToolDefinition$Outbound,
  SleepToolDefinition$outboundSchema,
} from "./sleeptooldefinition.js";
import {
  SttConfig,
  SttConfig$inboundSchema,
  SttConfig$Outbound,
  SttConfig$outboundSchema,
} from "./sttconfig.js";
import {
  TextConfiguration,
  TextConfiguration$inboundSchema,
  TextConfiguration$Outbound,
  TextConfiguration$outboundSchema,
} from "./textconfiguration.js";
import {
  ToolChoice,
  ToolChoice$inboundSchema,
  ToolChoice$Outbound,
  ToolChoice$outboundSchema,
} from "./toolchoice.js";
import {
  WebSearchPreviewToolDefinition,
  WebSearchPreviewToolDefinition$inboundSchema,
  WebSearchPreviewToolDefinition$Outbound,
  WebSearchPreviewToolDefinition$outboundSchema,
} from "./websearchpreviewtooldefinition.js";
import {
  WebSearchToolDefinition,
  WebSearchToolDefinition$inboundSchema,
  WebSearchToolDefinition$Outbound,
  WebSearchToolDefinition$outboundSchema,
} from "./websearchtooldefinition.js";

export type LivekitTokenRequestConversation = Conversation | string;

export type LivekitTokenRequestTools =
  | FunctionToolDefinition
  | ComputerUseToolDefinition
  | FileSearchToolDefinition
  | CodeInterpreterToolDefinition
  | CustomToolDefinition
  | WebSearchToolDefinition
  | MCPToolDefinition
  | ImageGenerationToolDefinition
  | LocalShellToolDefinition
  | ShellToolDefinition
  | WebSearchPreviewToolDefinition
  | HostedToolDefinition
  | SleepToolDefinition
  | HistoryToolDefinition;

/**
 * The truncation strategy for handling inputs that exceed the model's context window. 'auto' automatically truncates old messages to fit, 'disabled' returns an error if context is too long. Defaults to 'auto'.
 */
export const LivekitTokenRequestTruncation = {
  Auto: "auto",
  Disabled: "disabled",
} as const;
/**
 * The truncation strategy for handling inputs that exceed the model's context window. 'auto' automatically truncates old messages to fit, 'disabled' returns an error if context is too long. Defaults to 'auto'.
 */
export type LivekitTokenRequestTruncation = ClosedEnum<
  typeof LivekitTokenRequestTruncation
>;

/**
 * Specifies the processing tier for serving the request. 'auto' lets the system choose, 'default' uses standard processing, 'flex' allows flexible scheduling for lower priority, 'priority' provides faster processing. Defaults to 'auto'.
 */
export const LivekitTokenRequestServiceTier = {
  Auto: "auto",
  Default: "default",
  Flex: "flex",
  Priority: "priority",
} as const;
/**
 * Specifies the processing tier for serving the request. 'auto' lets the system choose, 'default' uses standard processing, 'flex' allows flexible scheduling for lower priority, 'priority' provides faster processing. Defaults to 'auto'.
 */
export type LivekitTokenRequestServiceTier = ClosedEnum<
  typeof LivekitTokenRequestServiceTier
>;

/**
 * LLM configuration for the voice session. Uses a subset of the responses create schema — fields like input, stream, background, webhooks, and store are excluded since the voice agent manages those. WARNING: This object is serialized into the LiveKit JWT metadata which is transmitted via HTTP headers. Keep payloads small — large tools arrays can exceed the ~8KB header size limit imposed by most servers and proxies.
 */
export type Llm = {
  /**
   * The ID of the model to use for generating the response.
   */
  model: string;
  /**
   * System or developer instructions inserted into the model's context before user input. Use this to guide the agent's behavior, set personality, define constraints, or provide domain-specific knowledge. Acts as a persistent system message for this response.
   */
  instructions?: string | undefined;
  /**
   * The conversation that this response belongs to. Can be a conversation ID string or a conversation object. Used to maintain context and history across multiple agent interactions. Optional - omit for one-off interactions.
   */
  conversation?: Conversation | string | null | undefined;
  /**
   * The unique ID of a previous response to continue from. Used for multi-turn conversations to maintain context and history. The agent will have access to all previous interactions in the chain.
   */
  previousResponseId?: string | undefined;
  prompt?: PromptTemplate | undefined;
  /**
   * Array of tool definitions that the agent can use during response generation. Supports built-in tools (web search, file search, code interpreter, computer use, image generation) and custom tools (functions, MCP, local shell). Each tool definition specifies its type and configuration.
   */
  tools?:
    | Array<
      | FunctionToolDefinition
      | ComputerUseToolDefinition
      | FileSearchToolDefinition
      | CodeInterpreterToolDefinition
      | CustomToolDefinition
      | WebSearchToolDefinition
      | MCPToolDefinition
      | ImageGenerationToolDefinition
      | LocalShellToolDefinition
      | ShellToolDefinition
      | WebSearchPreviewToolDefinition
      | HostedToolDefinition
      | SleepToolDefinition
      | HistoryToolDefinition
    >
    | undefined;
  toolChoice?: ToolChoice | undefined;
  /**
   * Whether to allow the agent to execute multiple tool calls in parallel. When true, the agent can make concurrent tool calls for efficiency. When false, tools are called sequentially. Defaults to true for better performance.
   */
  parallelToolCalls?: boolean | undefined;
  /**
   * The maximum total number of tool calls the agent can make during this response. Useful for controlling execution time and preventing infinite loops. Applies to all built-in tools like web search, file search, code interpreter, etc. Must be a positive integer between 1 and 300. Defaults to 64.
   */
  maxToolCalls?: number | undefined;
  reasoning?: ReasoningConfiguration | undefined;
  /**
   * The maximum number of tokens the model can generate in its output. Sets an upper bound to control costs and response length. The actual output may be shorter if the model finishes naturally. Must be a positive integer.
   */
  maxOutputTokens?: number | undefined;
  /**
   * Controls randomness in the model's output. Higher values (e.g., 1.5-2.0) make output more random and creative, lower values (e.g., 0.0-0.5) make it more focused and deterministic. Must be between 0 and 2.
   */
  temperature?: number | undefined;
  /**
   * Nucleus sampling parameter. The model considers only the tokens with top_p cumulative probability. Lower values (e.g., 0.1) make output more focused, higher values (e.g., 0.9) allow more diversity. Must be between 0 and 1. Alternative to temperature.
   */
  topP?: number | undefined;
  /**
   * The truncation strategy for handling inputs that exceed the model's context window. 'auto' automatically truncates old messages to fit, 'disabled' returns an error if context is too long. Defaults to 'auto'.
   */
  truncation?: LivekitTokenRequestTruncation | undefined;
  /**
   * Penalizes new tokens based on whether they appear in the text so far. Higher values increase the model's likelihood to talk about new topics.
   */
  presencePenalty?: number | undefined;
  /**
   * Penalizes new tokens based on their frequency in the text so far. Higher values decrease the model's likelihood to repeat the same line verbatim.
   */
  frequencyPenalty?: number | undefined;
  /**
   * Array of context management strategies to apply during response generation.
   */
  contextManagement?: Array<CompactionContextManagement> | undefined;
  /**
   * Specifies the processing tier for serving the request. 'auto' lets the system choose, 'default' uses standard processing, 'flex' allows flexible scheduling for lower priority, 'priority' provides faster processing. Defaults to 'auto'.
   */
  serviceTier?: LivekitTokenRequestServiceTier | undefined;
  /**
   * Custom metadata to attach to this response as key-value pairs. Keys must be strings up to 64 characters, values must be strings up to 512 characters. Maximum 16 pairs. Useful for tracking, categorization, or passing custom identifiers through your system.
   */
  metadata?: { [k: string]: string } | undefined;
  text?: TextConfiguration | undefined;
};

/**
 * Request parameters for generating a LiveKit room token with LLM configuration and optional STT settings.
 */
export type LivekitTokenRequest = {
  /**
   * LLM configuration for the voice session. Uses a subset of the responses create schema — fields like input, stream, background, webhooks, and store are excluded since the voice agent manages those. WARNING: This object is serialized into the LiveKit JWT metadata which is transmitted via HTTP headers. Keep payloads small — large tools arrays can exceed the ~8KB header size limit imposed by most servers and proxies.
   */
  llm: Llm;
  /**
   * Optional STT (speech-to-text) VAD configuration for the voice session.
   */
  stt?: SttConfig | undefined;
};

/** @internal */
export const LivekitTokenRequestConversation$inboundSchema: z.ZodType<
  LivekitTokenRequestConversation,
  z.ZodTypeDef,
  unknown
> = z.union([Conversation$inboundSchema, z.string()]);
/** @internal */
export type LivekitTokenRequestConversation$Outbound =
  | Conversation$Outbound
  | string;

/** @internal */
export const LivekitTokenRequestConversation$outboundSchema: z.ZodType<
  LivekitTokenRequestConversation$Outbound,
  z.ZodTypeDef,
  LivekitTokenRequestConversation
> = z.union([Conversation$outboundSchema, z.string()]);

export function livekitTokenRequestConversationToJSON(
  livekitTokenRequestConversation: LivekitTokenRequestConversation,
): string {
  return JSON.stringify(
    LivekitTokenRequestConversation$outboundSchema.parse(
      livekitTokenRequestConversation,
    ),
  );
}
export function livekitTokenRequestConversationFromJSON(
  jsonString: string,
): SafeParseResult<LivekitTokenRequestConversation, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => LivekitTokenRequestConversation$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'LivekitTokenRequestConversation' from JSON`,
  );
}

/** @internal */
export const LivekitTokenRequestTools$inboundSchema: z.ZodType<
  LivekitTokenRequestTools,
  z.ZodTypeDef,
  unknown
> = z.union([
  FunctionToolDefinition$inboundSchema,
  ComputerUseToolDefinition$inboundSchema,
  FileSearchToolDefinition$inboundSchema,
  CodeInterpreterToolDefinition$inboundSchema,
  CustomToolDefinition$inboundSchema,
  WebSearchToolDefinition$inboundSchema,
  MCPToolDefinition$inboundSchema,
  ImageGenerationToolDefinition$inboundSchema,
  LocalShellToolDefinition$inboundSchema,
  ShellToolDefinition$inboundSchema,
  WebSearchPreviewToolDefinition$inboundSchema,
  HostedToolDefinition$inboundSchema,
  SleepToolDefinition$inboundSchema,
  HistoryToolDefinition$inboundSchema,
]);
/** @internal */
export type LivekitTokenRequestTools$Outbound =
  | FunctionToolDefinition$Outbound
  | ComputerUseToolDefinition$Outbound
  | FileSearchToolDefinition$Outbound
  | CodeInterpreterToolDefinition$Outbound
  | CustomToolDefinition$Outbound
  | WebSearchToolDefinition$Outbound
  | MCPToolDefinition$Outbound
  | ImageGenerationToolDefinition$Outbound
  | LocalShellToolDefinition$Outbound
  | ShellToolDefinition$Outbound
  | WebSearchPreviewToolDefinition$Outbound
  | HostedToolDefinition$Outbound
  | SleepToolDefinition$Outbound
  | HistoryToolDefinition$Outbound;

/** @internal */
export const LivekitTokenRequestTools$outboundSchema: z.ZodType<
  LivekitTokenRequestTools$Outbound,
  z.ZodTypeDef,
  LivekitTokenRequestTools
> = z.union([
  FunctionToolDefinition$outboundSchema,
  ComputerUseToolDefinition$outboundSchema,
  FileSearchToolDefinition$outboundSchema,
  CodeInterpreterToolDefinition$outboundSchema,
  CustomToolDefinition$outboundSchema,
  WebSearchToolDefinition$outboundSchema,
  MCPToolDefinition$outboundSchema,
  ImageGenerationToolDefinition$outboundSchema,
  LocalShellToolDefinition$outboundSchema,
  ShellToolDefinition$outboundSchema,
  WebSearchPreviewToolDefinition$outboundSchema,
  HostedToolDefinition$outboundSchema,
  SleepToolDefinition$outboundSchema,
  HistoryToolDefinition$outboundSchema,
]);

export function livekitTokenRequestToolsToJSON(
  livekitTokenRequestTools: LivekitTokenRequestTools,
): string {
  return JSON.stringify(
    LivekitTokenRequestTools$outboundSchema.parse(livekitTokenRequestTools),
  );
}
export function livekitTokenRequestToolsFromJSON(
  jsonString: string,
): SafeParseResult<LivekitTokenRequestTools, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => LivekitTokenRequestTools$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'LivekitTokenRequestTools' from JSON`,
  );
}

/** @internal */
export const LivekitTokenRequestTruncation$inboundSchema: z.ZodNativeEnum<
  typeof LivekitTokenRequestTruncation
> = z.nativeEnum(LivekitTokenRequestTruncation);
/** @internal */
export const LivekitTokenRequestTruncation$outboundSchema: z.ZodNativeEnum<
  typeof LivekitTokenRequestTruncation
> = LivekitTokenRequestTruncation$inboundSchema;

/** @internal */
export const LivekitTokenRequestServiceTier$inboundSchema: z.ZodNativeEnum<
  typeof LivekitTokenRequestServiceTier
> = z.nativeEnum(LivekitTokenRequestServiceTier);
/** @internal */
export const LivekitTokenRequestServiceTier$outboundSchema: z.ZodNativeEnum<
  typeof LivekitTokenRequestServiceTier
> = LivekitTokenRequestServiceTier$inboundSchema;

/** @internal */
export const Llm$inboundSchema: z.ZodType<Llm, z.ZodTypeDef, unknown> = z
  .object({
    model: z.string(),
    instructions: z.string().optional(),
    conversation: z.nullable(z.union([Conversation$inboundSchema, z.string()]))
      .optional(),
    previous_response_id: z.string().optional(),
    prompt: PromptTemplate$inboundSchema.optional(),
    tools: z.array(
      z.union([
        FunctionToolDefinition$inboundSchema,
        ComputerUseToolDefinition$inboundSchema,
        FileSearchToolDefinition$inboundSchema,
        CodeInterpreterToolDefinition$inboundSchema,
        CustomToolDefinition$inboundSchema,
        WebSearchToolDefinition$inboundSchema,
        MCPToolDefinition$inboundSchema,
        ImageGenerationToolDefinition$inboundSchema,
        LocalShellToolDefinition$inboundSchema,
        ShellToolDefinition$inboundSchema,
        WebSearchPreviewToolDefinition$inboundSchema,
        HostedToolDefinition$inboundSchema,
        SleepToolDefinition$inboundSchema,
        HistoryToolDefinition$inboundSchema,
      ]),
    ).optional(),
    tool_choice: ToolChoice$inboundSchema.optional(),
    parallel_tool_calls: z.boolean().default(true),
    max_tool_calls: z.number().int().default(64),
    reasoning: ReasoningConfiguration$inboundSchema.optional(),
    max_output_tokens: z.number().int().optional(),
    temperature: z.number().optional(),
    top_p: z.number().optional(),
    truncation: LivekitTokenRequestTruncation$inboundSchema.default("auto"),
    presence_penalty: z.number().optional(),
    frequency_penalty: z.number().optional(),
    context_management: z.array(CompactionContextManagement$inboundSchema)
      .optional(),
    service_tier: LivekitTokenRequestServiceTier$inboundSchema.default("auto"),
    metadata: z.record(z.string()).optional(),
    text: TextConfiguration$inboundSchema.optional(),
  }).transform((v) => {
    return remap$(v, {
      "previous_response_id": "previousResponseId",
      "tool_choice": "toolChoice",
      "parallel_tool_calls": "parallelToolCalls",
      "max_tool_calls": "maxToolCalls",
      "max_output_tokens": "maxOutputTokens",
      "top_p": "topP",
      "presence_penalty": "presencePenalty",
      "frequency_penalty": "frequencyPenalty",
      "context_management": "contextManagement",
      "service_tier": "serviceTier",
    });
  });
/** @internal */
export type Llm$Outbound = {
  model: string;
  instructions?: string | undefined;
  conversation?: Conversation$Outbound | string | null | undefined;
  previous_response_id?: string | undefined;
  prompt?: PromptTemplate$Outbound | undefined;
  tools?:
    | Array<
      | FunctionToolDefinition$Outbound
      | ComputerUseToolDefinition$Outbound
      | FileSearchToolDefinition$Outbound
      | CodeInterpreterToolDefinition$Outbound
      | CustomToolDefinition$Outbound
      | WebSearchToolDefinition$Outbound
      | MCPToolDefinition$Outbound
      | ImageGenerationToolDefinition$Outbound
      | LocalShellToolDefinition$Outbound
      | ShellToolDefinition$Outbound
      | WebSearchPreviewToolDefinition$Outbound
      | HostedToolDefinition$Outbound
      | SleepToolDefinition$Outbound
      | HistoryToolDefinition$Outbound
    >
    | undefined;
  tool_choice?: ToolChoice$Outbound | undefined;
  parallel_tool_calls: boolean;
  max_tool_calls: number;
  reasoning?: ReasoningConfiguration$Outbound | undefined;
  max_output_tokens?: number | undefined;
  temperature?: number | undefined;
  top_p?: number | undefined;
  truncation: string;
  presence_penalty?: number | undefined;
  frequency_penalty?: number | undefined;
  context_management?: Array<CompactionContextManagement$Outbound> | undefined;
  service_tier: string;
  metadata?: { [k: string]: string } | undefined;
  text?: TextConfiguration$Outbound | undefined;
};

/** @internal */
export const Llm$outboundSchema: z.ZodType<Llm$Outbound, z.ZodTypeDef, Llm> = z
  .object({
    model: z.string(),
    instructions: z.string().optional(),
    conversation: z.nullable(z.union([Conversation$outboundSchema, z.string()]))
      .optional(),
    previousResponseId: z.string().optional(),
    prompt: PromptTemplate$outboundSchema.optional(),
    tools: z.array(
      z.union([
        FunctionToolDefinition$outboundSchema,
        ComputerUseToolDefinition$outboundSchema,
        FileSearchToolDefinition$outboundSchema,
        CodeInterpreterToolDefinition$outboundSchema,
        CustomToolDefinition$outboundSchema,
        WebSearchToolDefinition$outboundSchema,
        MCPToolDefinition$outboundSchema,
        ImageGenerationToolDefinition$outboundSchema,
        LocalShellToolDefinition$outboundSchema,
        ShellToolDefinition$outboundSchema,
        WebSearchPreviewToolDefinition$outboundSchema,
        HostedToolDefinition$outboundSchema,
        SleepToolDefinition$outboundSchema,
        HistoryToolDefinition$outboundSchema,
      ]),
    ).optional(),
    toolChoice: ToolChoice$outboundSchema.optional(),
    parallelToolCalls: z.boolean().default(true),
    maxToolCalls: z.number().int().default(64),
    reasoning: ReasoningConfiguration$outboundSchema.optional(),
    maxOutputTokens: z.number().int().optional(),
    temperature: z.number().optional(),
    topP: z.number().optional(),
    truncation: LivekitTokenRequestTruncation$outboundSchema.default("auto"),
    presencePenalty: z.number().optional(),
    frequencyPenalty: z.number().optional(),
    contextManagement: z.array(CompactionContextManagement$outboundSchema)
      .optional(),
    serviceTier: LivekitTokenRequestServiceTier$outboundSchema.default("auto"),
    metadata: z.record(z.string()).optional(),
    text: TextConfiguration$outboundSchema.optional(),
  }).transform((v) => {
    return remap$(v, {
      previousResponseId: "previous_response_id",
      toolChoice: "tool_choice",
      parallelToolCalls: "parallel_tool_calls",
      maxToolCalls: "max_tool_calls",
      maxOutputTokens: "max_output_tokens",
      topP: "top_p",
      presencePenalty: "presence_penalty",
      frequencyPenalty: "frequency_penalty",
      contextManagement: "context_management",
      serviceTier: "service_tier",
    });
  });

export function llmToJSON(llm: Llm): string {
  return JSON.stringify(Llm$outboundSchema.parse(llm));
}
export function llmFromJSON(
  jsonString: string,
): SafeParseResult<Llm, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Llm$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Llm' from JSON`,
  );
}

/** @internal */
export const LivekitTokenRequest$inboundSchema: z.ZodType<
  LivekitTokenRequest,
  z.ZodTypeDef,
  unknown
> = z.object({
  llm: z.lazy(() => Llm$inboundSchema),
  stt: SttConfig$inboundSchema.optional(),
});
/** @internal */
export type LivekitTokenRequest$Outbound = {
  llm: Llm$Outbound;
  stt?: SttConfig$Outbound | undefined;
};

/** @internal */
export const LivekitTokenRequest$outboundSchema: z.ZodType<
  LivekitTokenRequest$Outbound,
  z.ZodTypeDef,
  LivekitTokenRequest
> = z.object({
  llm: z.lazy(() => Llm$outboundSchema),
  stt: SttConfig$outboundSchema.optional(),
});

export function livekitTokenRequestToJSON(
  livekitTokenRequest: LivekitTokenRequest,
): string {
  return JSON.stringify(
    LivekitTokenRequest$outboundSchema.parse(livekitTokenRequest),
  );
}
export function livekitTokenRequestFromJSON(
  jsonString: string,
): SafeParseResult<LivekitTokenRequest, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => LivekitTokenRequest$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'LivekitTokenRequest' from JSON`,
  );
}
